import React from "react";

import BuildIcon from "@mui/icons-material/Build";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { keyframes } from "@mui/material";

import type { DeploymentEvent } from "src/domains/application/hooks/useDeploymentEvents";
import type { PipelineStep as PipelineStateStep } from "src/domains/application/hooks/useDeploymentStream";

// VCS is decoupled from the pipeline — its status comes from VcsConnection API,
// not from deployment events. Only build and deploy are pipeline steps.
export const PIPELINE_STEPS = ["build", "deploy"] as const;
export type PipelineStep = (typeof PIPELINE_STEPS)[number];

export const stepConfig: Record<
  PipelineStep,
  { label: string; icon: React.ReactElement; inProgressLabel: string }
> = {
  build: { label: "Build", icon: <BuildIcon />, inProgressLabel: "Building" },
  deploy: {
    label: "Deploy",
    icon: <RocketLaunchIcon />,
    inProgressLabel: "Deploying",
  },
};

export const statusChipColor: Record<
  string,
  "success" | "error" | "warning" | "info" | "default"
> = {
  completed: "success",
  in_progress: "info",
  Building: "info",
  Deploying: "info",
  failed: "error",
  pending: "warning",
  Queued: "warning",
};

export const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

export function getStepStatus(
  step: PipelineStep,
  events: DeploymentEvent[],
): { status: string; message: string } {
  const stepEvents = events.filter((e) => e.step === step);
  if (stepEvents.length === 0)
    return { status: "pending", message: "Waiting..." };
  const latest = stepEvents[0];
  return { status: latest.status, message: latest.message };
}

export function isStepStale(
  step: PipelineStep,
  events: DeploymentEvent[],
  pipelineState?: PipelineStateStep[] | null,
  thresholdMs = 15 * 60 * 1000,
): boolean {
  if (pipelineState && pipelineState.length > 0) {
    const stepState = pipelineState.find(
      (s) => s.step === step && s.status === "in_progress",
    );
    if (!stepState) return false;
    const elapsed = Date.now() - new Date(stepState.startedAt || 0).getTime();
    return elapsed > thresholdMs;
  }

  const stepEvents = events.filter(
    (e) => e.step === step && e.status === "in_progress",
  );
  if (stepEvents.length === 0) return false;
  const latestInProgress = stepEvents[0];
  const elapsed = Date.now() - new Date(latestInProgress.createdAt).getTime();
  return elapsed > thresholdMs;
}

export interface TemplateServiceInfo {
  name: string;
  type: string;
  baseImage?: string;
  buildCommand?: string;
  dockerfile?: string;
}

export interface PipelineTabProps {
  alias: string;
  templateSlug?: string;
  deployMode?: string;
  orgAlias?: string;
  vcsRepoUrl?: string | null;
  status?: string;
  projectAlias?: string;
  onProvisionVcs?: () => void;
  /** Called when the user promotes the current stack to another environment. */
  onPromote?: (targetEnvironmentSlug: string) => void;
}
